import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Typography, Row, Col, Statistic, DatePicker, Space, Select, Spin, Alert, Tag } from 'antd';
import Table from 'components/Common/Table';
import { FileTextOutlined, TeamOutlined, WalletOutlined } from '@ant-design/icons';
import { BarChart, Bar, PieChart, Pie, Cell, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ComposedChart } from 'recharts';
import axios from 'axios';
import useIsMobile from '../../hooks/useIsMobile';
import ReportFilterModal, { DatePresetButtons, MobileFilterButton, getDateRangeFromPreset } from '../../components/Reports/ReportFilterModal';
import ExportPdfButton from '../../components/Reports/ExportPdfButton';
import useUrlFilters from '../../hooks/useUrlFilters';
import { categoryFill, categoryLabel } from '../../utils/categoryColors';
import { EXPENSE_TYPES, expenseTypeOf } from '../../utils/expenseTypes';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const COLORS = ['#ff4d4f', '#fa8c16', '#faad14', '#52c41a', '#1890ff', '#722ed1', '#eb2f96', '#13c2c2'];

// Filters live in the URL so a filtered report can be shared as a link.
// Relative presets are kept as the preset name; only custom and FY ranges carry dates.
const URL_FILTERS = {
  preset: { type: 'string', default: 'all' },
  range: { type: 'dateRange', keys: ['start_date', 'end_date'] },
  category: { type: 'string', default: 'all' },
  expenseType: { type: 'string', key: 'expense_type', default: 'all' },
};
const PRESETS_WITH_DATES = ['custom', 'this_fy', 'last_fy'];
// Relative presets are recomputed from the name; a custom range with no dates is empty.
const resolveRange = (preset, range) =>
  (PRESETS_WITH_DATES.includes(preset) && range) || (preset === 'custom' ? null : getDateRangeFromPreset(preset));

const ExpenseReport = () => {
  const isMobile = useIsMobile();
  const reportRef = useRef(null);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const filterPreset = filters.preset;
  const dateRange = useMemo(() => resolveRange(filterPreset, filters.range), [filterPreset, filters.range]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const categoryFilter = filters.category;
  const setCategoryFilter = (value) => setFilters({ category: value });
  const expenseTypeFilter = filters.expenseType;
  const setExpenseTypeFilter = (value) => setFilters({ expenseType: value });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const useDateRange = filterPreset === 'custom' || filterPreset === 'this_fy' || filterPreset === 'last_fy';
      const params = {
        filter_preset: useDateRange ? undefined : (filterPreset === 'all' ? 'all_time' : filterPreset),
        start_date: useDateRange && dateRange && dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
        end_date: useDateRange && dateRange && dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
      };

      if (categoryFilter !== 'all') {
        params.category = categoryFilter;
      }

      if (expenseTypeFilter !== 'all') {
        params.expense_type = expenseTypeFilter;
      }

      const response = await axios.get('/accounting/expense/expense-analytics/', { params });

      if (response.status === 200) {
        setData(response.data);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.error || 'Failed to fetch expense data');
    } finally {
      setLoading(false);
    }
  }, [filterPreset, dateRange, categoryFilter, expenseTypeFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePresetChange = (preset) => {
    setFilters({
      preset,
      range: PRESETS_WITH_DATES.includes(preset) ? getDateRangeFromPreset(preset) : null,
    });
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setFilters({ preset: 'custom', range: [dates[0], dates[1]] });
    } else {
      setFilters({ preset: 'all', range: null });
    }
  };

  const handleClearFilters = () => {
    setFilters({ preset: 'all', range: null });
    setCategoryFilter('all');
    setExpenseTypeFilter('all');
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filterPreset !== 'all') count++;
    if (categoryFilter !== 'all') count++;
    if (expenseTypeFilter !== 'all') count++;
    return count;
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
      width: 110,
    },
    {
      title: 'Category',
      dataIndex: 'category_name',
      key: 'category',
      render: (name, record) => (
        <Tag color={record.category_color || 'default'}>{name || 'Uncategorized'}</Tag>
      ),
      width: 100,
    },
    {
      title: 'Expense Type',
      dataIndex: 'expense_type',
      key: 'expense_type',
      render: (expenseType) => {
        const { label, color } = expenseTypeOf(expenseType);
        return <Tag color={color}>{label}</Tag>;
      },
      width: 110,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (val) => <Text type="danger">₹{val?.toLocaleString('en-IN')}</Text>,
      sorter: (a, b) => a.amount - b.amount,
      align: 'right',
      width: 120,
    },
    {
      title: 'Vendor/Employee',
      dataIndex: 'vendor',
      key: 'vendor',
      render: (vendor, record) => {
        if (record.source === 'payroll') {
          return <span><TeamOutlined style={{ marginRight: 4, color: '#1890ff' }} />{record.employee_name}</span>;
        }
        return vendor;
      },
      width: 180,
      ellipsis: true,
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      render: (dept, record) => record.source === 'payroll' ? (dept || '-') : (record.account || '-'),
      width: 120,
      responsive: ['md'],
    },
    {
      title: 'Notes',
      dataIndex: 'public_notes',
      key: 'public_notes',
      ellipsis: true,
      responsive: ['lg'],
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      render: (source) => (
        <Tag color={source === 'payroll' ? 'blue' : 'green'}>
          {source === 'payroll' ? 'Payroll' : 'Expense'}
        </Tag>
      ),
      width: 90,
    },
  ];

  // Prepare chart data
  const categoryPieData = data?.by_category?.map((item, index) => ({
    name: categoryLabel(item),
    value: item.total,
    fill: categoryFill(item, index, COLORS)
  })) || [];

  // The API returns every vendor (ordered by spend); this chart shows the top
  // 10, as its title says. Slicing here keeps the payload complete for the
  // table and export while the chart stays readable.
  const VENDOR_CHART_LIMIT = 10;
  const vendorChartData = data?.by_vendor?.slice(0, VENDOR_CHART_LIMIT).map((item, index) => ({
    name: item.vendor?.length > 15 ? item.vendor.substring(0, 15) + '...' : item.vendor,
    total: item.total,
    fill: COLORS[index % COLORS.length]
  })) || [];

  // Tables for the PDF export. Passed explicitly rather than scraped from the
  // rendered tables so the export carries every row, not just the page on screen.
  const pdfTables = [
    {
      title: 'Expense Type Summary',
      columns: [
        { header: 'Expense Type', key: 'expense_type_display' },
        { header: 'Count', key: 'count', align: 'right' },
        { header: 'Total', key: 'total', align: 'right', format: 'currency' },
        { header: 'Share', key: 'percentage', align: 'right', format: 'percent' },
      ],
      rows: data?.by_expense_type || [],
    },
    {
      title: 'Monthly Trend by Expense Type',
      columns: [
        { header: 'Month', key: 'month' },
        { header: 'Variable', key: 'variable_expenses', align: 'right', format: 'currency' },
        { header: 'Non-Variable', key: 'non_variable_expenses', align: 'right', format: 'currency' },
        { header: 'Total', key: 'total', align: 'right', format: 'currency' },
      ],
      rows: data?.monthly_trend || [],
    },
    {
      title: 'Category Summary',
      columns: [
        { header: 'Category', key: 'category_name' },
        { header: 'Expense Type', key: 'expense_type_display' },
        { header: 'Count', key: 'count', align: 'right' },
        { header: 'Total', key: 'total', align: 'right', format: 'currency' },
        { header: 'Share', key: 'percentage', align: 'right', format: 'percent' },
      ],
      rows: data?.by_category || [],
    },
    {
      title: 'Vendors by Expense',
      columns: [
        { header: 'Vendor', key: 'vendor' },
        { header: 'Count', key: 'count', align: 'right' },
        { header: 'Total', key: 'total', align: 'right', format: 'currency' },
      ],
      rows: data?.by_vendor || [],
    },
    {
      title: 'Expense Details',
      columns: [
        { header: 'Date', key: 'date' },
        { header: 'Category', key: 'category_name' },
        { header: 'Expense Type', key: 'expense_type_display' },
        { header: 'Amount', key: 'amount', align: 'right', format: 'currency' },
        {
          header: 'Vendor/Employee',
          key: 'vendor',
          format: (value, row) => (row.source === 'payroll' ? row.employee_name : value) || '-',
        },
        {
          header: 'Department/Account',
          key: 'department',
          format: (value, row) => (row.source === 'payroll' ? value : row.account) || '-',
        },
        { header: 'Notes', key: 'public_notes' },
        { header: 'Source', key: 'source', format: (value) => (value === 'payroll' ? 'Payroll' : 'Expense') },
      ],
      rows: data?.expenses || [],
    },
  ];

  const pdfMeta = [
    { label: 'Category', value: categoryFilter === 'all' ? 'All Categories' : (data?.available_categories?.find(c => c.value === categoryFilter)?.label || categoryFilter) },
    { label: 'Expense Type', value: expenseTypeFilter === 'all' ? 'All Types' : expenseTypeOf(expenseTypeFilter).label },
    { label: 'Total Expenses', value: `Rs. ${(data?.summary?.total_expenses || 0).toLocaleString('en-IN')}` },
    { label: 'Variable', value: `Rs. ${(data?.summary?.total_variable_expenses || 0).toLocaleString('en-IN')}` },
    { label: 'Non-Variable', value: `Rs. ${(data?.summary?.total_non_variable_expenses || 0).toLocaleString('en-IN')}` },
    { label: 'Records', value: data?.summary?.expense_count || 0 },
  ];

  // Render additional filters for modal
  const renderAdditionalFilters = () => (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <div>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Category</Text>
        <Select
          value={categoryFilter}
          onChange={setCategoryFilter}
          style={{ width: '100%' }}
        >
          <Option value="all">All Categories</Option>
          {data?.available_categories?.map(cat => (
            <Option key={cat.value} value={cat.value}>{cat.label}</Option>
          ))}
        </Select>
      </div>
      <div>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Expense Type</Text>
        <Select
          value={expenseTypeFilter}
          onChange={setExpenseTypeFilter}
          style={{ width: '100%' }}
        >
          <Option value="all">All Types</Option>
          {EXPENSE_TYPES.map(type => (
            <Option key={type.value} value={type.value}>{type.label}</Option>
          ))}
        </Select>
      </div>
    </Space>
  );

  return (
    <div ref={reportRef} style={{
      height: 'calc(100vh - 64px)',
      overflow: 'auto',
      padding: isMobile ? '12px' : '16px 24px',
      backgroundColor: '#f0f2f5'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <Title level={isMobile ? 4 : 2} style={{ margin: 0 }}>Expense Report</Title>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <ExportPdfButton
            reportRef={reportRef}
            title="Expense Report"
            subtitle={data?.summary?.date_range}
            meta={pdfMeta}
            tables={pdfTables}
            fileName="expense-report"
            orientation="landscape"
            disabled={loading || !data}
          />
          {isMobile && (
            <MobileFilterButton
              onClick={() => setFilterModalVisible(true)}
              activeFiltersCount={getActiveFiltersCount()}
            />
          )}
        </div>
      </div>

      {/* Desktop Filters */}
      {!isMobile && (
        <Card size="small" style={{ marginBottom: '16px' }}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <div>
              <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Date Range:</Text>
              <DatePresetButtons filterPreset={filterPreset} onPresetChange={handlePresetChange} />
            </div>
            <Space wrap size="middle">
              <div>
                <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Custom Date Range:</Text>
                <RangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  format="YYYY-MM-DD"
                  size="small"
                  allowClear
                  placeholder={['Start Date', 'End Date']}
                />
              </div>
              <div>
                <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Category:</Text>
                <Select
                  value={categoryFilter}
                  onChange={setCategoryFilter}
                  size="small"
                  style={{ width: 180 }}
                >
                  <Option value="all">All Categories</Option>
                  {data?.available_categories?.map(cat => (
                    <Option key={cat.value} value={cat.value}>{cat.label}</Option>
                  ))}
                </Select>
              </div>
              <div>
                <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Expense Type:</Text>
                <Select
                  value={expenseTypeFilter}
                  onChange={setExpenseTypeFilter}
                  size="small"
                  style={{ width: 150 }}
                >
                  <Option value="all">All Types</Option>
                  {EXPENSE_TYPES.map(type => (
                    <Option key={type.value} value={type.value}>{type.label}</Option>
                  ))}
                </Select>
              </div>
            </Space>
          </Space>
        </Card>
      )}

      {/* Mobile Filter Modal */}
      <ReportFilterModal
        visible={filterModalVisible}
        onClose={() => setFilterModalVisible(false)}
        filterPreset={filterPreset}
        dateRange={dateRange}
        onChange={({ preset, range }) => setFilters({
          preset,
          range: PRESETS_WITH_DATES.includes(preset) ? range : null,
        })}
        onClearFilters={handleClearFilters}
        title="Expense Report Filters"
      >
        {renderAdditionalFilters()}
      </ReportFilterModal>

      {error && (
        <Alert message="Error" description={error} type="error" closable showIcon onClose={() => setError(null)} style={{ marginBottom: '16px' }} />
      )}

      {loading && (
        <Card size="small">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}><Text>Loading expense data...</Text></div>
          </div>
        </Card>
      )}

      {!loading && data && (
        <>
          {/* Summary Statistics */}
          <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
            <Col xs={12} sm={8} md={4}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Total Expenses</span>}
                  value={data.summary?.total_expenses || 0}
                  prefix="₹"
                  precision={0}
                  valueStyle={{ fontSize: isMobile ? '16px' : '22px', color: '#ff4d4f' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Payroll</span>}
                  value={data.summary?.total_payroll || 0}
                  prefix={<><TeamOutlined style={{ marginRight: 4 }} />₹</>}
                  precision={0}
                  valueStyle={{ fontSize: isMobile ? '16px' : '22px', color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Other Expenses</span>}
                  value={data.summary?.total_regular_expenses || 0}
                  prefix={<><WalletOutlined style={{ marginRight: 4 }} />₹</>}
                  precision={0}
                  valueStyle={{ fontSize: isMobile ? '16px' : '22px', color: '#fa8c16' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Expense Count</span>}
                  value={data.summary?.expense_count || 0}
                  prefix={<FileTextOutlined />}
                  valueStyle={{ fontSize: isMobile ? '16px' : '22px' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Variable</span>}
                  value={data.summary?.total_variable_expenses || 0}
                  prefix="₹"
                  precision={0}
                  valueStyle={{ fontSize: isMobile ? '16px' : '22px', color: '#fa541c' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Non-Variable</span>}
                  value={data.summary?.total_non_variable_expenses || 0}
                  prefix="₹"
                  precision={0}
                  valueStyle={{ fontSize: isMobile ? '16px' : '22px', color: '#2f54eb' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Charts Row */}
          <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
            {/* Monthly Trend Chart */}
            <Col xs={24} md={14}>
              <Card size="small" title={<Text strong>Monthly Expense Trend</Text>}>
                {data.monthly_trend && data.monthly_trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={isMobile ? 220 : 280}>
                    <ComposedChart data={data.monthly_trend} margin={{ top: 20, right: isMobile ? 10 : 30, left: isMobile ? 0 : 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: isMobile ? 9 : 11 }} angle={-45} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: isMobile ? 9 : 11 }} width={isMobile ? 40 : 60} />
                      <Tooltip formatter={(value) => `₹${value?.toLocaleString('en-IN')}`} />
                      {!isMobile && <Legend />}
                      <Bar dataKey="payroll" stackId="expenses" fill="#1890ff" name="Payroll" />
                      <Bar dataKey="regular_expenses" stackId="expenses" fill="#fa8c16" name="Other Expenses" />
                      <Line type="monotone" dataKey="total" stroke="#ff4d4f" strokeWidth={2} name="Total" dot={{ r: isMobile ? 2 : 3 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px' }}><Text type="secondary">No trend data available</Text></div>
                )}
              </Card>
            </Col>

            {/* Category Pie Chart */}
            <Col xs={24} md={10}>
              <Card size="small" title={<Text strong>Expenses by Category</Text>}>
                {categoryPieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={isMobile ? 220 : 280}>
                    <PieChart>
                      <Pie
                        data={categoryPieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={isMobile ? 35 : 50}
                        outerRadius={isMobile ? 65 : 90}
                        paddingAngle={2}
                        dataKey="value"
                        label={isMobile ? false : ({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        labelLine={isMobile ? false : { length: 10, length2: 5 }}
                      >
                        {categoryPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `₹${value?.toLocaleString('en-IN')}`} />
                      {isMobile && <Legend />}
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px' }}><Text type="secondary">No category data available</Text></div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Top Vendors Chart */}
          {vendorChartData.length > 0 && !isMobile && (
            <Card size="small" title={<Text strong>Top 10 Vendors by Expense</Text>} style={{ marginBottom: '16px' }}>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={vendorChartData} layout="vertical" margin={{ top: 20, right: 30, left: 80, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} />
                  <Tooltip formatter={(value) => `₹${value?.toLocaleString('en-IN')}`} />
                  <Bar dataKey="total" name="Total Expense">
                    {vendorChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* Expense Type Summary Table */}
          {data.by_expense_type && data.by_expense_type.length > 0 && (
            <Card size="small" title={<Text strong>Expense Type Summary</Text>} style={{ marginBottom: '16px' }}>
              <Table
                dataSource={data.by_expense_type}
                rowKey="expense_type"
                size="small"
                pagination={false}
                scroll={{ x: isMobile ? 400 : undefined }}
                columns={[
                  { title: 'Expense Type', dataIndex: 'expense_type', key: 'expense_type', render: (expenseType) => { const { label, color } = expenseTypeOf(expenseType); return <Tag color={color}>{label}</Tag>; } },
                  { title: 'Count', dataIndex: 'count', key: 'count', align: 'right', width: 80 },
                  { title: 'Total', dataIndex: 'total', key: 'total', render: (v) => `₹${v?.toLocaleString('en-IN')}`, align: 'right', width: 120 },
                  { title: 'Share', dataIndex: 'percentage', key: 'percentage', render: (v) => `${v}%`, align: 'right', width: 80 },
                ]}
              />
            </Card>
          )}

          {/* Category Summary Table */}
          {data.by_category && data.by_category.length > 0 && (
            <Card size="small" title={<Text strong>Category Summary</Text>} style={{ marginBottom: '16px' }}>
              <Table
                dataSource={data.by_category}
                rowKey="category"
                size="small"
                pagination={false}
                scroll={{ x: isMobile ? 400 : undefined }}
                columns={[
                  { title: 'Category', dataIndex: 'category_name', key: 'category', render: (name, record) => <Tag color={record.category_color || 'default'}>{name || 'Uncategorized'}</Tag> },
                  { title: 'Expense Type', dataIndex: 'expense_type', key: 'expense_type', width: 120, render: (expenseType) => { const { label, color } = expenseTypeOf(expenseType); return <Tag color={color}>{label}</Tag>; } },
                  { title: 'Count', dataIndex: 'count', key: 'count', align: 'right', width: 80 },
                  { title: 'Total', dataIndex: 'total', key: 'total', render: (v) => `₹${v?.toLocaleString('en-IN')}`, align: 'right', width: 120 },
                  { title: 'Share', dataIndex: 'percentage', key: 'percentage', render: (v) => `${v}%`, align: 'right', width: 80 },
                ]}
              />
            </Card>
          )}

          {/* Expense Details Table */}
          <Card size="small" title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <Text strong>Expense Details</Text>
              {!isMobile && <Text type="secondary" style={{ fontSize: '12px' }}>{data.summary?.date_range}</Text>}
            </div>
          }>
            <Table
              columns={columns}
              dataSource={data.expenses || []}
              rowKey="id"
              size="small"
              pagination={{
                defaultPageSize: isMobile ? 5 : 10,
                pageSizeOptions: ['5', '10', '20', '50'],
                showSizeChanger: !isMobile,
                showTotal: isMobile ? undefined : (total, range) => `${range[0]}-${range[1]} of ${total}`,
                simple: isMobile,
              }}
              scroll={{ x: 'max-content' }}
            />
          </Card>
        </>
      )}

      {!loading && !data && !error && (
        <Card size="small">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Text type="secondary">No expense data available.</Text>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ExpenseReport;
